import moment from "moment";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Table, TableColumnsType } from "antd";
import { ApprovalLineType, ApprovalStatusType, ExpensePageType, FINANCIAL_EDIT_PERMISSION, FINANCIAL_TEAM_CODE } from "../../../../constans";
import Button from "../../../Button";
import { IApprovalComment, IApprovalLine, IEmployeeValue, IGetExpense, IGetExpenseLine } from "../../../../models/financials.model";
import useAxios from "../../../../hooks/useAxios";
import { deleteAdminExpense, deleteExpense, getApprovalLine, getExpense, updateApprovalStatus } from "../../../../api/financials.api";
import { useEffect, useState } from "react";
import { getCommonCodeLabel } from "../../../../lib/store";
import { RootState } from "../../../../store/rootReducer";
import { IResponse } from "../../../../models/common.api.model";
import ConfirmModal from "../../../modals/ConfirmModal";
import { ISubcode } from "../../../../models/codes.model";
import useNotification from "../../../../hooks/useNotification";

interface IProps {
    expenseCode: number,
    commonCode: string,
    setPageType: (param: ExpensePageType) => void;
    getExpenseList: (listType: 'A' | 'P') => void;
}

const ExpenseRead = ({expenseCode, commonCode, setPageType, getExpenseList} : IProps) => {

    const { t, i18n } = useTranslation();
    const { auth } = useSelector((state: RootState) => state.auth);
    const { successNotification, errorNotification } = useNotification();

    const { data: expenseDetail, isLoading, error, execute: executeDetail } = useAxios<IGetExpense>();
    const { data: editStatusResult, isLoading : loading1, error : editError, execute: executeEditAppStatus } = useAxios<IResponse<Number>>();
    const { data: delExpenseResult, isLoading : loading2, error : delError, execute: executeDelExpense } = useAxios<IResponse<Number>>();
    const { data: expenseLine, isLoading : loading3, error : lineError, execute: executeLine } = useAxios<IGetExpenseLine>();

    const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
    const [confirmAdminDeleteVisible, setConfirmAdminDeleteVisible] = useState(false);
    //결재라인
    const [selectedApprovalList, setSelectedApprovalList] = useState<Map<string, IEmployeeValue>>(new Map<string, IEmployeeValue>());
    const [approvalListData, setApprovalListData] = useState<any>([]);
    //수신참조
    const [selectedCCList, setSelectedCCList] = useState<Map<string, IEmployeeValue>>(new Map<string, IEmployeeValue>());
    const [ccListData, setCCListData] = useState<any>();
    //결재의견
    const [approvalCommentList, setApprovalCommentList] = useState<IApprovalComment[]>([]);

    const { commonCodes } = useSelector((state: RootState) => state.commonCode);
    // FA0007 = 결재라인 기본값
    const FA0007 = commonCodes.find(item => item.commonCode === 'FA0007')?.detailCode;
    // FA0008 = 수신참조 기본값
    const FA0008 = commonCodes.find(item => item.commonCode === 'FA0008')?.detailCode;
    
    useEffect(() => {
        getExpenseDetail();
        getExpenseLine();
    }, [expenseCode]);

    useEffect(() => {
        const appFinal : Map<string, IEmployeeValue> = new Map<string, IEmployeeValue>();
        const ccFinal : Map<string, IEmployeeValue> = new Map<string, IEmployeeValue>();
        const tempCommentList : IApprovalComment[] = [];

        if(expenseLine?.data) {
            expenseLine?.data.forEach((item) => {
                const memberCode = item.memberCode;
                const campusName = item.campusName
                const memberName = item.memberName + '(' + item.memberEngName + ')';
                if(item.lineType === ApprovalLineType.APPROVAL_LINE) {
                    let tempGroupName = getDefualtGroupName(FA0007, memberCode, campusName);
                    appFinal.set(memberCode, {campusCode:item.campusCode, campusName:tempGroupName, memberName:memberName, confirmStatus:item.confirmStatus, lastUpdDttm:item.lastUpdDttm});
                } else if(item.lineType === ApprovalLineType.CC_LINE) {
                    let tempGroupName = getDefualtGroupName(FA0008, memberCode, campusName);
                    ccFinal.set(memberCode, {campusCode:item.campusCode, campusName:tempGroupName, memberName:memberName});
                }

                if(item.lineType === ApprovalLineType.APPROVAL_LINE && item.comment) {
                    tempCommentList.push({memberCode:memberCode, memberName:memberName, comment:item.comment, approvalDate:item.approvalDate});
                }
            });
        }

        setSelectedApprovalList(appFinal);
        setSelectedCCList(ccFinal);
        setApprovalCommentList(tempCommentList);
    },[expenseLine]);

    useEffect(() => {
        let tempAppList : any = [];
        
        let orderSeq = 1;
        selectedApprovalList.forEach((value, key) => {
            const confirmStatusName = value.confirmStatus === ApprovalStatusType.WAIT ? '' : getCommonCodeLabel('FA0005', value.confirmStatus, i18n.language);
            tempAppList.push({memberCode:key, orderSeq:orderSeq, campusName:value.campusName, memberName:value.memberName
                , confirmStatus:value.confirmStatus, confirmStatusName:confirmStatusName, lastUpdDttm:value.lastUpdDttm});
            orderSeq++;
        });
        
        setApprovalListData(tempAppList);
    },[selectedApprovalList]);

    useEffect(() => {
        let tempAppList : any = [];
        
        let orderSeq = 1;
        selectedCCList.forEach((value, key) => {
            tempAppList.push({memberCode:key, orderSeq:orderSeq, campusName:value.campusName, memberName:value.memberName})
            orderSeq++;
        });
        
        setCCListData(tempAppList);
    },[selectedCCList]);

    useEffect(() => {
        if(editStatusResult) {
            if(editStatusResult.result === "success") {
                getExpenseList('A');
                setPageType(ExpensePageType.UPDATE);
            }
        }
    }, [editStatusResult]);

    useEffect(() => {
        if(delExpenseResult) {
            if(delExpenseResult.result === "success") {
                successNotification(t('common.message.delete'));

                getExpenseList('A');
                setPageType(ExpensePageType.CREATE);
            }
        }
    }, [delExpenseResult]);

    useEffect(() => {
        if(delError) {
            if (!delError?.response?.status) return;
            if(delError.response?.data.data === 'CC') {
                errorNotification(t('payment.income.error1'));
            } else if(delError.response?.data.data === 'CA') {
                errorNotification(t('payment.expense.error1'));
            } else {
                errorNotification(t('common.message.error'));
            }
        }
    }, [delError]);

    const getExpenseDetail = () => {
        executeDetail(getExpense(expenseCode, commonCode));
    }

    const getExpenseLine = () => {
        executeLine(getApprovalLine(expenseCode));
    }

    //기본 참조,결재자 그룹명(직책) 변경
    const getDefualtGroupName = (lineList : ISubcode[] | undefined, memberCode : string, groupName : string) => {
        let tempGroupName = groupName;
        if(lineList && lineList.length > 0) {
            lineList.map((item) => {
                if(item.detailCode === memberCode) {
                    if(item.korName.indexOf('|') > -1 &&  item.engName.indexOf('|') > -1) {
                        tempGroupName = item.korName.split('|')[1];
                    }
                }
                return item;
            });
        }
        return tempGroupName;
    }

    const onEditButton = () => {
        if(expenseDetail?.data?.approvalStatus === ApprovalStatusType.WAIT) {
            //파라미터 셋팅(form)
            const form = new FormData();
            form.append('approvalStatus', ApprovalStatusType.CANCLE);        
            
            executeEditAppStatus(updateApprovalStatus(expenseCode, form));
        } else {
            setPageType(ExpensePageType.UPDATE);
        }
    }

    const onDeleteButton = () => {
        //파라미터 셋팅(form)
        executeDelExpense(deleteExpense(expenseCode));
    }

    const onAdminDeleteButton = () => {
        executeDelExpense(deleteAdminExpense(expenseCode));
    }

    //마감취소:금액수정
    const onAmountEditButton = () => {
        setPageType(ExpensePageType.AMOUNT_UPDATE);
    }

    const appLinecolumns: TableColumnsType<IApprovalLine> = [
        {
            title: t('system.menu.column1'),
            dataIndex: 'orderSeq',
            key: 'orderSeq',
            align: 'center',
        },
        {
            title: t('campus.staff.column38'),
            dataIndex: 'campusName',
            key: 'campusName',
            align: 'center',
        },
        {
            title: t('class.class.column74'),
            dataIndex: 'memberName',
            key: 'memberName',
            align: 'center',
        },
        {
            title: t('payment.expense.column10'),
            dataIndex: 'confirmStatusName',
            key: 'confirmStatusName',
            align: 'center',
            width: 90,
            render: (value, record) => {
                if(record.confirmStatus === 'CO' || record.confirmStatus === 'RJ') {
                    return <>{value} <br /> {moment(record.lastUpdDttm).format('YYYY.MM.DD')} <br /> {moment(record.lastUpdDttm).format('HH:mm')}</>
                } else {
                    return value
                }
            }
        },
    ];

    const appCCLinecolumns: TableColumnsType<IApprovalLine> = [
        {
            title: t('campus.staff.column38'),
            dataIndex: 'campusName',
            key: 'campusName',
            align: 'center',
        },
        {
            title: t('class.class.column74'),
            dataIndex: 'memberName',
            key: 'memberName',
            align: 'center',
        },
    ];

    return(
        <>
            {/*
            <div className="comm-bx__between">  
                <h3 className='sub-title'>{t('payment.income.title3')}</h3>
            </div>
            */}
            <div className="w-100 flex">
                <div style={{width:'60%'}}>
                    <div className="comm-bx__space"> 
                        <div><h3 className="sub-title">{t('payment.expense.title1')}</h3></div>
                    </div>
                    <div className="comm-table__area app-Line__area">
                        <Table 
                            className='approvalLineTable'
                            columns={appLinecolumns}
                            dataSource={approvalListData}
                            rowKey={'memberCode'}
                            pagination={{
                                current: 1,
                                pageSize: 999999999,
                            }}
                        />
                    </div>
                </div>
                <div style={{width:'40%'}} className="ml-2">
                    <div className="comm-bx__space"> 
                        <div><h3 className="sub-title">{t('payment.expense.title2')}</h3></div>
                    </div>
                    <div className="comm-table__area app-Line__area">
                        <Table 
                            columns={appCCLinecolumns}
                            dataSource={ccListData}
                            rowKey={'memberCode'}
                            pagination={{
                                current: 1,
                                pageSize: 999999999,
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="comm-bx__between">  
                <h3 className='sub-title'>{t('payment.expense.title4')}</h3>
            </div>
            <div className="comm-table__area rowType">
                <table>
                    <colgroup>
                        <col width="15%" />
                        <col width="85%" />
                    </colgroup>
                    <tbody>
                        <tr>
                            <th>{t('system.treecode.account1')}</th>
                            <td>
                                {i18n.language === 'ko' ? expenseDetail?.data?.firstExpenseAccName : expenseDetail?.data?.firstExpenseAccEngName}
                            </td>
                        </tr>
                        <tr>
                            <th>{t('system.treecode.account2')}</th>
                            <td>
                                {i18n.language === 'ko' ? expenseDetail?.data?.secondExpenseAccName : expenseDetail?.data?.secondExpenseAccEngName}
                            </td>
                        </tr>
                        <tr>
                            <th>{t('system.treecode.account3')}</th>
                            <td>
                                {i18n.language === 'ko' ? expenseDetail?.data?.thirdExpenseAccName : expenseDetail?.data?.thirdExpenseAccEngName} 
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="comm-table__area rowType">
                <table>
                    <colgroup>
                        <col width="15%" />
                        <col width="35%" />
                        <col width="15%" />
                        <col width="35%" />
                    </colgroup>
                    <tbody>
                        <tr>
                            <th>{t('student.student.62')}</th>
                            <td>{moment(expenseDetail?.data?.registDate).format("YYYY-MM-DD")}</td>
                            <th>{t('payment.expense.column1')}</th>
                            <td>{moment(expenseDetail?.data?.expenseDate).format("YYYY-MM-DD")}</td>
                        </tr>
                        <tr>
                            <th>{t('common.column.title')}</th>
                            <td colSpan={3}>
                                <div className="w-100">
                                    {expenseDetail?.data?.title}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>{t('payment.expense.label6')}</th>
                            <td colSpan={3}>
                                <textarea
                                    className="w-100"
                                    rows={5}
                                    value={expenseDetail?.data?.summary}
                                />
                            </td>
                        </tr>
                        <tr>
                            <th>{t('payment.expense.column4')}</th>
                            <td>
                                {getCommonCodeLabel('FA0002', expenseDetail?.data?.expenseMtd, i18n.language)}
                            </td>
                            <th>{t('payment.expense.label5')}</th>
                            <td>
                                {getCommonCodeLabel('FA0003', expenseDetail?.data?.expenseYn, i18n.language)}
                            </td>
                        </tr>
                        <tr>
                            <th>{t('payment.income.column3')}</th>
                            <td>
                                {getCommonCodeLabel('FB0004', expenseDetail?.data?.currency, i18n.language)}
                            </td>
                            <th>{t('student.student.column97')}</th>
                            <td>
                                <div className="flex" style={{alignItems:'center', gap:'5px'}}>
                                    {expenseDetail?.data?.amount.toLocaleString()}
                                    <b>{t('campus.setting.text2')}</b>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>{t('payment.expense.column5')}</th>
                            <td colSpan={3}>
                                {expenseDetail?.data?.clientName}
                            </td>
                        </tr>
                        <tr>
                            <th>{t('payment.expense.column6')}</th>
                            <td>
                                {expenseDetail?.data?.clientDepositor}
                            </td>
                            <th>{t('homepage.member.20')}</th>
                            <td>
                                {expenseDetail?.data?.clientTelNo}
                            </td>
                        </tr>
                        <tr>
                            <th>{t('campus.shuttle.column12')}</th>
                            <td>
                                {getCommonCodeLabel('SY0007', expenseDetail?.data?.clientBankCode, i18n.language)}
                            </td>
                            <th>{t('campus.shuttle.column13')}</th>
                            <td>
                                {expenseDetail?.data?.clientAccNo}
                            </td>
                        </tr>
                        <tr>
                            <th>{t('payment.expense.label2')}</th>
                            <td>
                                {getCommonCodeLabel('FA0004', expenseDetail?.data?.proofType, i18n.language)}
                            </td>
                            <th>{t('payment.expense.label3')}</th>
                            <td>
                                {moment(expenseDetail?.data?.proofDate).format("YYYY-MM-DD")}
                            </td>
                        </tr>
                        <tr>
                            <th>{t('common.column.file')}</th>
                            <td colSpan={3}>
                                {
                                    expenseDetail?.data?.files
                                        ? (<>
                                            {
                                                expenseDetail?.data.files.map((item: any) => (
                                                    <a
                                                      className="download-link btn-text mr-1"
                                                      href={item.fileLocation}
                                                      download={item.originalName}
                                                    >
                                                      {item.originalName}
                                                    </a>
                                                ))
                                            } 
                                        </>) 
                                    : null
                                }
                            </td>
                        </tr>
                        <tr>
                            <th>{t('common.text.note')}</th>
                            <td colSpan={3}>
                                <textarea
                                    className="w-100"
                                    rows={5}
                                    value={expenseDetail?.data?.note}
                                />
                            </td>
                        </tr>
                        {
                            approvalCommentList.length > 0?
                            (
                                <tr>
                                    <th>{t('payment.expense.label7')}</th>
                                    <td colSpan={3}>
                                    {
                                        approvalCommentList?.map((item, idx) => {
                                            return ( 
                                                <div key={idx} className="approvalComment">
                                                    <div className="appCommentInner">
                                                        <span className="appCommentSpan1">{item.memberName}</span>
                                                        <span className="appCommentSpan2">{item.comment}</span> 
                                                        <span className="appCommentSpan3">{item.approvalDate}</span>
                                                    </div>
                                                </div>
                                            );
                                        })   
                                    }
                                    </td>
                                </tr>
                            ): null
                        }
                    </tbody>
                </table>
            </div>
            {(expenseDetail?.data?.memberCode === auth.memberCode && expenseDetail?.data?.completeYn === 'N') &&
                <div className="comm-bx__between">
                    <div></div>
                    <div className="comm-btn__area">
                        {
                            (expenseDetail?.data?.approvalStatus === ApprovalStatusType.CANCLE) &&
                            <Button
                                onClick={()=>{setConfirmDeleteVisible(true)}}
                                className="btn-basic pink-line"
                                text={t('common.button.delete')}
                                /*writeAuthYn={writeAuthYn}*/
                            />
                        }
                        {
                            (expenseDetail?.data?.approvalStatus === ApprovalStatusType.WAIT || expenseDetail?.data?.approvalStatus === ApprovalStatusType.CANCLE) &&
                            <Button
                                onClick={()=>{onEditButton();}}
                                className="btn-basic pink-line"
                                text={t('common.button.edit')}
                                /*writeAuthYn={writeAuthYn}*/
                            />
                        }
                        {
                            (expenseDetail?.data?.approvalStatus === ApprovalStatusType.REJECT) &&
                            <Button
                                onClick={()=>{onEditButton();}}
                                className="btn-basic pink-line"
                                text={t('payment.expense.btn4')}
                                /*writeAuthYn={writeAuthYn}*/
                            />
                        }
                        
                    </div>
                </div>
            }
            {(auth.groupNo === FINANCIAL_TEAM_CODE || FINANCIAL_EDIT_PERMISSION.includes(auth.memberCode)) &&
                <div className="comm-bx__between">
                    <div></div>
                    <div className="comm-btn__area">
                        {expenseDetail?.data?.completeYn === 'N' && (
                            <Button
                                    onClick={()=>{setConfirmAdminDeleteVisible(true)}}
                                    className="btn-basic gray"
                                    text={t('common.button.delete')}
                            />
                        )}
                        <Button
                            onClick={()=>{onAmountEditButton()}}
                            className="btn-basic pink-line"
                            text={(expenseDetail?.data?.completeYn === 'Y')?t('payment.expense.btn8'):t('payment.expense.btn9')}
                            /*writeAuthYn={writeAuthYn}*/
                        />
                    </div>
                </div>
            }
        {confirmDeleteVisible && (
            <ConfirmModal
            visible={confirmDeleteVisible}
            setVisible={setConfirmDeleteVisible}
            onConfirm={onDeleteButton}
            titleText={t('system.menu.title6')}
            messageText={t('system.menu.text3')}
            confirmText={t('common.button.delete')}
            cancelText={t('common.button.cancel')}
            />
        )}

        {confirmAdminDeleteVisible && (
            <ConfirmModal
            visible={confirmAdminDeleteVisible}
            setVisible={setConfirmAdminDeleteVisible}
            onConfirm={onAdminDeleteButton}
            titleText={t('system.menu.title6')}
            messageText={t('system.menu.text3')}
            confirmText={t('common.button.delete')}
            cancelText={t('common.button.cancel')}
            />
        )}
        </>
    )
}

export default ExpenseRead;